// src/components/rag/CompactAppCard.tsx
import React from 'react';
import { Card } from 'react-bootstrap';
import { Play } from 'lucide-react';
import { Application } from '../../types/knowledge-base';
import AccessWarning from './AccessWarning';

interface CompactAppCardProps {
    app: Application & {
        accessInfo?: {
            hasRagAccess: boolean;
            accessibleIndexes: string[];
        };
    };
    onChat: (app: { id: string; name: string }) => void;
    hideStatusBadge?: boolean; // New prop to control badge visibility
}

const CompactAppCard: React.FC<CompactAppCardProps> = ({ app, onChat, hideStatusBadge = false }) => {
    // Render access warning if needed
    const renderAccessMessage = () => {
        if (!app.accessInfo) return null;

        const { hasRagAccess, accessibleIndexes } = app.accessInfo;

        if (!hasRagAccess) {
            return <AccessWarning type="noAccess" />;
        }

        if (accessibleIndexes.length === 0) {
            return <AccessWarning type="noKBAccess" />;
        }

        if (accessibleIndexes.length < (app.index_ids?.length || 0)) {
            return (
                <AccessWarning
                    type="partialAccess"
                    indexCount={accessibleIndexes.length}
                    totalIndexes={app.index_ids?.length}
                />
            );
        }

        return null;
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Card className="mb-3 border shadow-sm">
            <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                        <div className="d-flex align-items-center">
                            <h6 className="card-title mb-1">{app.name}</h6>
                            {/* Status badge shown conditionally */}
                            {!hideStatusBadge && (
                                <span className={`ms-2 badge px-2 py-1 text-xs ${
                                    app.enabled ? 'bg-success-subtle text-success-emphasis' : 'bg-danger-subtle text-danger-emphasis'
                                }`}>
                                    {app.enabled ? 'Enabled' : 'Disabled'}
                                </span>
                            )}
                        </div>
                        <div className="d-flex flex-column text-muted small mt-1">
                            <span>
                                {app.created_at === app.updated_at
                                    ? `Created on ${formatDate(app.created_at)}`
                                    : `Updated on ${formatDate(app.updated_at)}`}
                            </span>
                            <div className="mt-1">
                                {renderAccessMessage()}
                            </div>
                        </div>
                    </div>
                    {app.accessInfo?.hasRagAccess && app.accessInfo.accessibleIndexes.length > 0 && (
                        <button
                            className="btn btn-outline-primary btn-sm ms-2"
                            disabled={!app.enabled}
                            onClick={() => onChat({ id: app.id, name: app.name })}
                            title={!app.enabled ? 'Application is disabled' : 'Start chat'}
                        >
                            <Play size={14} className="me-1" />
                            Chat
                        </button>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default CompactAppCard;